import { useState } from "react";
import axios from "axios";
import { Star, Send, CheckCircle, AlertCircle, User, MessageSquare, Phone, Mail } from "lucide-react";

const BACKOFFICE_API = "https://backoffice.bddstudiodentistico.it/api";

const QUESTIONS = [
  { id: "q1", title: "Attesa", description: "Come valuti i tempi di attesa?" },
  { id: "q2", title: "Qualità", description: "Come valuti la qualità della prestazione?" },
  { id: "q3", title: "Igiene", description: "Come valuti l'igiene dello studio?" },
  { id: "q4", title: "Accoglienza", description: "Come ti sei sentito accolto dal nostro staff?" },
  { id: "q5", title: "Valutazione generale", description: "Nel complesso, che voto dai alla tua esperienza?" },
];

function RatingSelector({ value, onChange, questionId }) {
  const [hovered, setHovered] = useState(0);
  const labels = ["Pessimo", "Scarso", "Sufficiente", "Buono", "Ottimo"];

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex gap-1.5 sm:gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            data-testid={`rating-${questionId}-${star}`}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(star)}
            className="group transition-transform hover:scale-110 active:scale-95"
          >
            <Star
              className={`w-8 h-8 sm:w-10 sm:h-10 transition-colors ${
                star <= (hovered || value)
                  ? "text-amber-400 fill-amber-400 drop-shadow-sm"
                  : "text-slate-200 hover:text-amber-200"
              }`}
            />
          </button>
        ))}
      </div>
      <div className="flex justify-between w-full px-1">
        <span className="text-[10px] text-slate-400">Pessimo</span>
        <span className="text-xs font-medium text-slate-500 h-4">
          {hovered ? labels[hovered - 1] : value ? labels[value - 1] : ""}
        </span>
        <span className="text-[10px] text-slate-400">Ottimo</span>
      </div>
    </div>
  );
}

export default function FeedbackFormPage() {
  const [patientName, setPatientName] = useState("");
  const [patientType, setPatientType] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [ratings, setRatings] = useState({});
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const allAnswered = QUESTIONS.every((q) => ratings[q.id] >= 1) && !!patientType;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allAnswered) {
      setError("Rispondi a tutte le domande e seleziona il tipo paziente prima di inviare.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        patient_name: patientName.trim(),
        patient_type: patientType,
        phone: phone.trim(),
        email: email.trim(),
        answers: QUESTIONS.map((q) => ({ question_id: q.id, rating: ratings[q.id] })),
        comment: comment.trim(),
      };
      await axios.post(`${BACKOFFICE_API}/feedback/submit`, payload);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || "Errore nell'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-lg p-8 sm:p-12 max-w-md w-full text-center" data-testid="feedback-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Grazie!</h2>
          <p className="text-slate-500">Il tuo feedback è stato inviato con successo. Apprezziamo il tempo che ci hai dedicato.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 sm:py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight" data-testid="feedback-form-title">
            La tua opinione conta
          </h1>
          <p className="text-slate-500 mt-2 text-sm sm:text-base">
            Aiutaci a migliorare i nostri servizi con il tuo feedback
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nome paziente */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-slate-400" />
              <label className="text-sm font-semibold text-slate-600">Nome (opzionale)</label>
            </div>
            <input
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              placeholder="Il tuo nome..."
              data-testid="feedback-patient-name"
              className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
            />
          </div>

          {/* Telefono + Email (aggiunti 07/2026): permettono al backend di
              associare in modo più affidabile il feedback al contatto GHL
              (matching per email/telefono → chiave primaria vs nome). */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-slate-400" />
                <label className="text-sm font-semibold text-slate-600">Telefono (opzionale)</label>
              </div>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="es. 3331234567"
                data-testid="feedback-phone"
                className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-slate-400" />
                <label className="text-sm font-semibold text-slate-600">Email (opzionale)</label>
              </div>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="es. nome@esempio.it"
                data-testid="feedback-email"
                className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
              />
            </div>
          </div>

          {/* Tipo paziente */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <label className="text-sm font-semibold text-slate-600 mb-3 block">Tipo paziente (opzionale)</label>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="patient-type-principale"
                onClick={() => setPatientType(patientType === "principale" ? "" : "principale")}
                className={`flex-1 h-11 rounded-xl text-sm font-semibold transition-all ${
                  patientType === "principale"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
                }`}
              >
                Principale
              </button>
              <button
                type="button"
                data-testid="patient-type-secondario"
                onClick={() => setPatientType(patientType === "secondario" ? "" : "secondario")}
                className={`flex-1 h-11 rounded-xl text-sm font-semibold transition-all ${
                  patientType === "secondario"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
                }`}
              >
                Secondario
              </button>
            </div>
          </div>

          {/* Domande */}
          {QUESTIONS.map((q, idx) => (
            <div
              key={q.id}
              className={`bg-white rounded-2xl shadow-sm border p-5 transition-colors ${
                ratings[q.id] ? "border-emerald-200" : "border-slate-100"
              }`}
              data-testid={`question-card-${q.id}`}
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                  {idx + 1}
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-700">{q.title}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">{q.description}</p>
                </div>
              </div>
              <RatingSelector
                value={ratings[q.id] || 0}
                onChange={(val) => setRatings((prev) => ({ ...prev, [q.id]: val }))}
                questionId={q.id}
              />
            </div>
          ))}

          {/* Commento */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-slate-400" />
              <label className="text-sm font-semibold text-slate-600">Commento (opzionale)</label>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Vuoi aggiungere qualcosa?"
              rows={3}
              data-testid="feedback-comment"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors resize-none"
            />
          </div>

          {/* Errore */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-3 text-sm" data-testid="feedback-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Pulsante invio */}
          <button
            type="submit"
            disabled={submitting || !allAnswered}
            data-testid="feedback-submit-btn"
            className={`w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
              allAnswered && !submitting
                ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-600/20 active:scale-[0.98]"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Invio in corso...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Invia feedback
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
